/** @jsx jsx */
/* eslint-disable jsx-a11y/label-has-associated-control, max-len */
import React, { useEffect, useState } from 'react';
import { css, jsx } from '@emotion/core';
import Modal from 'components/molecules/Modal';
import { saveSetting } from 'api/shiftBoard';
import {
  BoardSetting, PersonalBoardSetting, TimeFormat, ViewType,
} from 'domain/attend/shiftBoard/types';
import { useEmploymentOptions } from 'hooks/useEmploymentOptions';
import { HIDE_SUBMISSIONS } from 'domain/attend/shiftBoard/phase';
import { boardStyles, mock } from './styles';
import BoardSelect from './BoardSelect';

export type BoardSettingsModalProps = {
  open: boolean;
  /** 店舗単位の設定（T1） */
  setting: BoardSetting;
  /** 店舗の設定を変えてよいか（シフト表の編集権限）。自分の表示は誰でも変えられる */
  editable: boolean;
  onClose: () => void;
  /** storeSaved: 店舗の設定を保存したか（自分の表示だけなら盤面は読み直さなくてよい。10/5） */
  onSaved: (setting: BoardSetting, storeSaved: boolean) => void;
  /** 自分だけの表示設定（人単位）。いま実際に効いている値 */
  personal: PersonalBoardSetting & { defaultView: ViewType; slotMinutes: number };
  onSavePersonal: (next: Partial<PersonalBoardSetting>) => Promise<void>;
};

const styles = {
  section: css({
    border: `1px solid ${mock.gray10}`,
    borderRadius: '4px',
    padding: '12px 14px 4px',
    marginBottom: '14px',
    background: '#fff',
  }),
  sectionHead: css({
 display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '10px',
}),
  sectionTitle: css({ fontSize: '13px', fontWeight: 700, color: mock.textMain }),
  sectionNote: css({ fontSize: '11px', color: mock.textSub }),
};

/**
 * シフト編成の設定（9/23 に「自分の表示」と「店舗の設定」に分けた）
 *
 * - 自分の表示: 既定のビュー・日ビューの刻み・時間の表記。人単位で保存し、他の人には影響しない。
 *   誰でも変えられる（見やすさの好みなので編集権限は問わない）。
 * - 店舗の設定: 提出対象だけ。組織単位で、店舗の全員に効く。シフト表の編集権限がある人だけ変えられる。
 *   提出対象は提出状況でしか使わないので、フェーズ 1 では見出しごと出さない。
 *
 * 1 人あたり売上・人件費率の目標・反映時に再計算しない は 9/23 に外した。
 */
const BoardSettingsModal: React.FC<BoardSettingsModalProps> = ({
  open, setting, editable, onClose, onSaved, personal, onSavePersonal,
}) => {
  const [draft, setDraft] = useState<BoardSetting>(setting);
  const [mine, setMine] = useState(personal);
  const [error, setError] = useState<string | null>(null);
  const employmentOptions = useEmploymentOptions();

  useEffect(() => {
    if (open) {
      setDraft({ ...setting, submitEmploymentIds: [...(setting.submitEmploymentIds || [])] });
      setMine(personal);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    try {
      // 自分の表示は誰でも保存できる
      await onSavePersonal({ defaultView: mine.defaultView, slotMinutes: mine.slotMinutes, timeFormat: mine.timeFormat });
      if (editable && !HIDE_SUBMISSIONS) {
        // 店舗の設定。いまは提出対象だけ（表示の項目は人単位に移したので送らない）
        const saved = await saveSetting({
          orgCode: draft.orgCode,
          submitEmploymentIds: draft.submitEmploymentIds,
        });
        onSaved(saved, true);
      } else {
        onSaved(setting, false);
      }
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.message) || '保存できませんでした。');
    }
  };

  return (
    <Modal open={open} closeHandler={onClose} title="シフト編成の設定" submitText="保存" onSubmit={submit} width="560px">
      {/* 本文が低いと「開いたときの表示」の一覧（共通の Select）が本文の枠で切れるので、高さを残す（10/7） */}
      <div css={boardStyles.modalBody} style={{ minHeight: '240px' }}>
        {/* 自分の表示（人単位）。見出しと説明文は 9/24 に外した */}
        <div css={styles.section}>
          <div css={boardStyles.formRow}>
            <label htmlFor="st-view">開いたときの表示</label>
            {/* ほかの画面と同じ共通の Select（10/7） */}
            <BoardSelect
              id="st-view"
              value={mine.defaultView}
              onChange={(v) => setMine({ ...mine, defaultView: v as ViewType })}
              options={[
                { value: 'day', label: '日' },
                { value: 'week', label: '週' },
                { value: 'half', label: '半月' },
                { value: 'month', label: '1ヶ月' },
              ]}
              style={{ width: '200px' }}
            />
          </div>
          <div css={boardStyles.formRow}>
            <label htmlFor="st-slot">日ビューの刻み</label>
            <BoardSelect
              id="st-slot"
              value={String(mine.slotMinutes)}
              onChange={(v) => setMine({ ...mine, slotMinutes: parseInt(v, 10) })}
              options={[{ value: '15', label: '15分' }, { value: '30', label: '30分' }]}
              style={{ width: '200px' }}
            />
          </div>
          <div css={boardStyles.formRow}>
            <label htmlFor="st-timeformat">時間の表記</label>
            <BoardSelect
              id="st-timeformat"
              value={mine.timeFormat}
              onChange={(v) => setMine({ ...mine, timeFormat: v as TimeFormat })}
              options={[
                { value: 'auto', label: '勤怠の設定に合わせる' },
                { value: 'hhmm', label: '60進（7:30）' },
                { value: 'decimal', label: '10進（7.50）' },
              ]}
              style={{ width: '200px' }}
            />
          </div>
        </div>

        {/* 店舗の設定は、いまは提出対象だけ。提出状況と一緒にフェーズ 1 では見出しごと出さない（9/23） */}
        {!HIDE_SUBMISSIONS && (
          <div css={styles.section} style={{ opacity: editable ? 1 : 0.6 }}>
            <div css={styles.sectionHead}>
              <span css={styles.sectionTitle}>店舗の設定</span>
              <span css={styles.sectionNote}>
                {editable ? 'この店舗の全員に反映されます' : 'シフト表の編集権限がある人だけ変更できます'}
              </span>
            </div>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>希望シフトの提出対象（雇用形態。未選択なら全員）</div>
            <div style={{ maxHeight: '140px', overflowY: 'auto', marginBottom: '8px' }}>
              {employmentOptions.filter((o) => o.value).map((o) => (
                <label key={o.value} htmlFor={`st-emp-${o.value}`} css={boardStyles.listRow} style={{ minHeight: '32px' }}>
                  <input
                    id={`st-emp-${o.value}`}
                    type="checkbox"
                    checked={draft.submitEmploymentIds.indexOf(o.value) !== -1}
                    disabled={!editable}
                    onChange={(e) => setDraft({
                      ...draft,
                      submitEmploymentIds: e.target.checked ? [...draft.submitEmploymentIds, o.value] : draft.submitEmploymentIds.filter((v) => v !== o.value),
                    })}
                  />
                  <span>{o.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
        {error && <div css={boardStyles.dangerText}>{error}</div>}
      </div>
    </Modal>
  );
};

export default BoardSettingsModal;
